import { z } from 'zod';

// Website forms (/api/forms). Schemas check shape only: whether a family, event, question or member
// exists, field keys and type compatibility stay in formsService. The lists below mirror
// models/FormEdition, models/FamilyField, models/FormEvent and config/formFamilies (the tests compare them).

export const FormEditionStatus = z.enum(['new', 'active', 'ignored', 'waiting']);
export type FormEditionStatus = z.infer<typeof FormEditionStatus>;

export const FormFamilyKey = z.enum([
  'registration',
  'event_registration',
  'fellow_application',
  'fellow_institution',
  'session_feedback',
  'development_plan',
  'trainee_survey',
  'pi_self_id',
  'committee_application',
]);
export type FormFamilyKey = z.infer<typeof FormFamilyKey>;

export const FormFieldType = z.enum(['yes/no', 'choice', 'number', 'date', 'text']);
export type FormFieldType = z.infer<typeof FormFieldType>;

/** A question's mapping when it is not a family field: kept as an extra answer, or not stored. */
export const FormMappingSpecial = z.enum(['_extra', '_skip']);
export type FormMappingSpecial = z.infer<typeof FormMappingSpecial>;

export const FormEventMode = z.enum(['online', 'in_person', 'hybrid']);
export type FormEventMode = z.infer<typeof FormEventMode>;

export const FormQuestionAction = z.enum(['field', 'existing', 'extra', 'skip']);
export type FormQuestionAction = z.infer<typeof FormQuestionAction>;

export const FormMissingAction = z.enum(['confirm', 'dismiss']);
export type FormMissingAction = z.infer<typeof FormMissingAction>;

export const FormResponseFilter = z.enum([
  'unmatched',
  'name_only',
  'non_member',
  'matched',
  'unclassified',
  'missing',
  'flagged',
]);
export type FormResponseFilter = z.infer<typeof FormResponseFilter>;

export const FormSyncMode = z.enum(['nightly', 'backfill']);
export type FormSyncMode = z.infer<typeof FormSyncMode>;

const Text = z.string().nullish();
const Digits = z.string().regex(/^\d+$/, 'must be a whole number');
const Year = z.union([z.number().int(), Digits, z.literal(''), z.null()]).optional();
const OptionalEventId = z.union([z.guid(), z.literal('')]).nullish();

/** A new event created with the decision; the service ignores it without a title. */
export const FormEventInput = z.strictObject({
  title: z.string().optional(),
  startsAt: Text,
  endsAt: Text,
  location: Text,
  mode: z.union([FormEventMode, z.literal('')]).nullish(),
});
export type FormEventInput = z.input<typeof FormEventInput>;

// ---------------------------------------------------------------- requests

export const FormEditionIdParams = z.strictObject({ id: z.guid() });
export type FormEditionIdParams = z.input<typeof FormEditionIdParams>;

export const FormQuestionParams = z.strictObject({ id: z.guid(), questionId: z.string().min(1) });
export type FormQuestionParams = z.input<typeof FormQuestionParams>;

/** Unknown keys answer 404 Family not found. */
export const FormFamilyParams = z.strictObject({ key: z.string() });
export type FormFamilyParams = z.input<typeof FormFamilyParams>;

export const FormResponseIdParams = z.strictObject({ id: z.guid() });
export type FormResponseIdParams = z.input<typeof FormResponseIdParams>;

export const FormTombstoneIdParams = z.strictObject({ id: z.guid() });
export type FormTombstoneIdParams = z.input<typeof FormTombstoneIdParams>;

/** GET /api/forms/editions?status= */
export const FormEditionListQuery = z.strictObject({ status: z.union([FormEditionStatus, z.literal('')]).optional() });
export type FormEditionListQuery = z.input<typeof FormEditionListQuery>;

/** GET /api/forms/responses; page and limit are clamped by the route (limit up to 200, default 50). */
export const FormResponseListQuery = z.strictObject({
  familyKey: z.string().optional(),
  editionId: z.union([z.guid(), z.literal('')]).optional(),
  filter: z.union([FormResponseFilter, z.literal('')]).optional(),
  page: Digits.optional(),
  limit: Digits.optional(),
});
export type FormResponseListQuery = z.input<typeof FormResponseListQuery>;

/** POST /api/forms/editions/:id/accept */
export const AcceptEditionRequest = z.strictObject({
  familyKey: FormFamilyKey,
  label: Text,
  year: z.number().int().nullish(),
  cohort: Text,
  eventId: OptionalEventId,
  event: FormEventInput.nullish(),
});
export type AcceptEditionRequest = z.input<typeof AcceptEditionRequest>;

/** POST /api/forms/editions/:id/family */
export const ChangeFamilyRequest = z.strictObject({ familyKey: FormFamilyKey });
export type ChangeFamilyRequest = z.input<typeof ChangeFamilyRequest>;

/** PATCH /api/forms/editions/:id; only the fields sent change, blank clears. */
export const UpdateEditionRequest = z.strictObject({
  label: Text,
  year: Year,
  cohort: Text,
  eventId: OptionalEventId,
  event: FormEventInput.nullish(),
});
export type UpdateEditionRequest = z.input<typeof UpdateEditionRequest>;

/** POST /api/forms/editions/:id/questions/:questionId */
export const ResolveQuestionRequest = z.strictObject({
  action: FormQuestionAction,
  key: z.string().optional(),
  label: z.string().optional(),
  type: FormFieldType.optional(),
  sensitive: z.boolean().optional(),
});
export type ResolveQuestionRequest = z.input<typeof ResolveQuestionRequest>;

/** POST /api/forms/responses/:id/link — a member id, or an email exactly one member has. */
export const LinkResponseRequest = z.strictObject({
  userId: z.union([z.guid(), z.literal('')]).nullish(),
  email: Text,
});
export type LinkResponseRequest = z.input<typeof LinkResponseRequest>;

/** POST /api/forms/responses/:id/missing; confirming needs a reason (the service checks). */
export const ResolveMissingRequest = z.strictObject({
  action: FormMissingAction,
  reason: Text,
});
export type ResolveMissingRequest = z.input<typeof ResolveMissingRequest>;

/** POST /api/forms/events */
export const CreateFormEventRequest = FormEventInput;
export type CreateFormEventRequest = z.input<typeof CreateFormEventRequest>;

/** POST /api/forms/sync */
export const FormSyncRequest = z.strictObject({ mode: FormSyncMode.optional() });
export type FormSyncRequest = z.input<typeof FormSyncRequest>;
